import { useGlobalErrorStore } from 'scbk-services/error';
import { NativeBridgeOptions, NativeBridgeResponse } from 'scbk-services/types/bridge/global';
import { useNativeBridgeCallbackRegistryStore } from '../stores/nativeBridgeCallbackRegistry';
import { useNativeBridge } from './useNativeBridge';

/**
 * 공동인증서 발급/재발급 요청값
 */
export interface CertIssueRequest {
    certCredential: string;
    refNum: string;
    authCode: string;
    ca: string;
    type: string;
    caUrl: string;
    caPort: string;
    serverKey: string; // 보안키보드 publicKey
}
/**
 * 공동인증서 발급/재발급 응답값
 */
export interface CertIssueResponse {
    result: string;
}

/**
 * 인증서 서명 요청값
 */
export interface CertSignRequest {
    storage: string; // 저장소분류코드('0':wizvera, '1':ubikey)
    type: string; // 인증서 타입
    index?: number; // 인증서 순서
    serial: string; // 인증서 일련번호
    certCredential: string; // 인증서 비밀번호
    serverKey: string; // 보안키보드 publicKey
    plainData: string; // 서명데이터
}

/**
 * 인증서 서명 응답값
 */
export interface CertSignResponse {
    result: string; // 서명결과 (0: 성공, 1: 비밀번호오류, ...기타오류)
    pkcs7SignedData: string;
    randomVid: string;
}

/**
 * 인증서 목록 요청값
 */
export interface CertListRequest {
    oidFilter?: string;
    purpose?: '0' | '1'; // 인증서 목록 조회 대상(0:전체 / 1: inAPP인증서만(ubikey인증서 제외))
}
/**
 * 인증서 목록 응답값
 */
export interface CertListResponse {
    certList: NCertInfo[];
}

// 인증서 정보
export type NCertInfo = {
    user: string; // 인증서 주체(subjectDn)
    issuer: string; // 인증서 발급기관(ex: yessignCA-Test Class 5)
    storagetype?: string; // 저장소 타입(ex: 'wizvera', 'ubikey')  [IOS: undefined]
    expiredate: string; // 인증서 만료일(format: 'yyyyMMdd')
    serial: string; // 인증서 일련번호(length: 7)
    startdate: string; // 인증서 발급일(format: 'yyyyMMdd')
    certType: string; // 인증서 용도 (ex: '금융(개인)')
    type: string; // 인증서 타입(ex: '0':wizvera, '1':ubikey)
    index: number; // 인증서 순서(type: number)
    renewableYN?: string; // 만료일 30일 이전 판단
};

/**
 * 인증서 가져오기 요청 요청값
 */
export interface ReqCertImportRequest {
    authNum: string;
}
/**
 * 인증서 가져오기 요청 응답값
 */
export interface ReqCertImportResponse {
    result: string;
    p7signed: string;
}

/**
 * 인증서 가져오기 저장 요청 요청값
 */
export interface ReqCertImportSaveRequest {
    authNum: string;
    certCredential: string;
    serverKey: string; // 보안키보드 publicKey
    p7signed: string;
}
/**
 * 인증서 가져오기 저장 요청 응답값
 */
export interface ReqCertImportSaveResponse {
    result: string;
}

/**
 * 인증서 내보내기 인증번호 요청 요청값
 */
export interface ReqCertExportAuthNumRequest {
    autnNumLen: string;
    index: number;
    serial: string;
    certCredential: string;
    serverKey: string;
}
/**
 * 인증서 내보내기 인증번호 요청 응답값
 */
export interface ReqCertExportAuthNumResponse {
    result: string;
    authNum: string;
}

/**
 * 인증서 내보내기 결과 요청 요청값
 */
export interface ReqCertExportResultRequest {}
/**
 * 인증서 내보내기 결과 요청 응답값
 */
export interface ReqCertExportResultResponse {
    result: string;
}

/**
 * 공동인증서 갱신 요청값
 */
export interface CertUpdateRequest {
    certCredentialOld: string;
    certCredentialNew: string;
    certIndex: number;
    ca: string;
    caUrl: string;
    caPort: string;
    serverKey: string; // 보안키보드 publicKey
}
/**
 * 공동인증서 갱신 응답값
 */
export interface CertUpdateResponse {
    result: string;
}

/**
 * 공동인증서 폐기 요청값
 */
export interface CertDeleteRequest {
    index: number;
    serial: string;
}
/**
 * 공동인증서 폐기 응답값
 */
export interface CertDeleteResponse {
    result: string;
}

/**
 * 공동인증서 상세정보 요청값
 */
export interface CertInfoRequest {
    index: number;
}
/**
 * 공동인증서 상세정보 응답값
 */
export interface CertInfoResponse {
    result: string;
    info: {
        version: string;
        serialNo: string;
        signatureAlgorithm: string;
        issuer: string;
        startdate: string;
        expirationdate: string;
        subject: string;
        publicKeyAlgorithm: string;
        thumbprintAlgorithm: string;
        policy: string;
        crlDistributionPoint: string;
        aias: string;
    };
}

/**
 * 공동인증서 비밀번호 변경 요청값
 */
export interface CertPasswordChangeRequest {
    index: number;
    certCredentialOld: string;
    certCredentialNew: string;
    serverKey: string; // 보안키보드 publicKey
}
/**
 * 공동인증서 비밀번호 변경 응답값
 */
export interface CertPasswordChangeResponse {
    result: string;
}

/**
 * Certificate 네이티브 브릿지 hook
 * @returns
 */
export const useCertificateBridge = () => {
    const nativeBridge = useNativeBridge();
    const errorStore = useGlobalErrorStore();

    /**
     * 공동인증서 발급/재발급
     * @param request
     * @returns
     */
    const doNCertIssue = async (
        request: CertIssueRequest,
        options?: NativeBridgeOptions<CertIssueResponse>,
    ): Promise<CertIssueResponse | undefined> => {
        const res = await nativeBridge<CertIssueRequest, CertIssueResponse>(
            'doNCertIssue',
            request,
        );

        if (res.status === 'fail' || res.status === 'error') {
            const error = res.error!;
            if (options?.onError && error) {
                options.onError(error);
            } else {
                errorStore.setErrorInfo({ code: error.code, title: error.message });
            }
            return;
        }

        if (options?.onSuccess && res.payload) {
            options.onSuccess(res.payload);
            return;
        }

        return res.payload;
    };

    /**
     * 인증서 서명
     * @param request
     * @returns
     */
    const doNCertSign = async (
        request?: CertSignRequest,
        options?: NativeBridgeOptions<CertSignResponse>,
    ): Promise<CertSignResponse | undefined> => {
        const res = await nativeBridge<CertSignRequest, CertSignResponse>('doNCertSign', request);

        if (res.status === 'fail' || res.status === 'error') {
            const error = res.error!;
            if (options?.onError && error) {
                options.onError(error);
            } else {
                errorStore.setErrorInfo({ code: error.code, title: error.message });
            }
            return;
        }

        if (options?.onSuccess && res.payload) {
            options.onSuccess(res.payload);
            return;
        }

        return res.payload;
    };

    /**
     * 인증서 목록
     * @param request
     * @returns
     */
    const doNCertList = async (
        request?: CertListRequest,
        options?: NativeBridgeOptions<CertListResponse>,
    ): Promise<CertListResponse | undefined> => {
        if (!request?.oidFilter) {
            request = { oidFilter: '', ...request };
        }
        if (!request?.purpose) {
            request = { purpose: '0', ...request };
        }

        const res = await nativeBridge<CertListRequest, CertListResponse>('doNCertList', request);

        if (res.status === 'fail' || res.status === 'error') {
            const error = res.error!;
            if (options?.onError && error) {
                options.onError(error);
            } else {
                errorStore.setErrorInfo({ code: error.code, title: error.message });
            }
            return;
        }

        let certList: NCertInfo[] = [];
        try {
            console.log('doNCertList - res : ', res);
            if (!res.payload) {
                throw new Error('doNCertList - not found payload');
            }

            console.log('doNCertList - payload type : ', typeof res.payload);
            console.log('doNCertList - certList type : ', typeof res.payload?.certList);

            // TODO : Android 인증서 리스트 string으로 넘어와 임시 대응
            if (typeof res.payload?.certList === 'string') {
                console.warn('payload?.certList is string type!');
                certList = JSON.parse(res.payload?.certList ?? '');
            } else {
                certList = res.payload?.certList;
            }
        } catch (error) {
            console.error('doNCertList parse - error : ', error);
        }

        return { certList };
    };

    /**
     * 인증서 가져오기 요청
     * @param request
     * @returns
     */
    const doNReqCertImport = async (
        request: ReqCertImportRequest,
        options?: NativeBridgeOptions<ReqCertImportResponse>,
    ): Promise<ReqCertImportResponse | undefined> => {
        const res = await nativeBridge<ReqCertImportRequest, ReqCertImportResponse>(
            'doNReqCertImport',
            request,
        );

        if (res.status === 'fail' || res.status === 'error') {
            const error = res.error!;
            if (options?.onError && error) {
                options.onError(error);
            } else {
                errorStore.setErrorInfo({ code: error.code, title: error.message });
            }
            return;
        }

        if (options?.onSuccess && res.payload) {
            options.onSuccess(res.payload);
            return;
        }

        return res.payload;
    };

    /**
     * 인증서 가져오기 저장 요청
     * @param request
     * @returns
     */
    const doNReqCertImportSave = async (
        request: ReqCertImportSaveRequest,
        options?: NativeBridgeOptions<ReqCertImportSaveResponse>,
    ): Promise<ReqCertImportSaveResponse | undefined> => {
        const res = await nativeBridge<ReqCertImportSaveRequest, ReqCertImportSaveResponse>(
            'doNReqCertImportSave',
            request,
        );

        if (res.status === 'fail' || res.status === 'error') {
            const error = res.error!;
            if (options?.onError && error) {
                options.onError(error);
            } else {
                errorStore.setErrorInfo({ code: error.code, title: error.message });
            }
            return;
        }

        if (options?.onSuccess && res.payload) {
            options.onSuccess(res.payload);
            return;
        }

        return res.payload;
    };

    /**
     * 인증서 내보내기 인증번호 요청
     * @param request
     * @returns
     */
    const doNReqCertExportAuthNum = async (
        request: ReqCertExportAuthNumRequest,
        options?: NativeBridgeOptions<ReqCertExportAuthNumResponse>,
    ): Promise<ReqCertExportAuthNumResponse | undefined> => {
        const res = await nativeBridge<ReqCertExportAuthNumRequest, ReqCertExportAuthNumResponse>(
            'doNReqCertExportAuthNum',
            request,
        );

        if (res.status === 'fail' || res.status === 'error') {
            const error = res.error!;
            if (options?.onError && error) {
                options.onError(error);
            } else {
                errorStore.setErrorInfo({ code: error.code, title: error.message });
            }
            return;
        }

        if (options?.onSuccess && res.payload) {
            options.onSuccess(res.payload);
            return;
        }

        return res.payload;
    };

    const { registerCallback } = useNativeBridgeCallbackRegistryStore.getState();
    /**
     * 인증서 내보내기 결과 요청
     * @param request
     * @returns
     */
    const doNReqCertExportResult = (callback: (result: string | '0' | '1') => void) => {
        registerCallback('doNReqCertExportResult', {
            once: true,
            fn: (res: NativeBridgeResponse<ReqCertExportResultResponse>) => {
                const { payload, status } = res;

                if (status === 'error' || status === 'fail') {
                    const error = res.error!;
                    errorStore.setErrorInfo({ code: error.code, title: error.message });
                    return;
                }

                if (!payload) {
                    return;
                }

                const { result } = payload;
                callback(result);
            },
        });
    };

    /**
     * 공동인증서 갱신
     * @param request
     * @returns
     */
    const doNCertUpdate = async (
        request: CertUpdateRequest,
        options?: NativeBridgeOptions<CertUpdateResponse>,
    ): Promise<CertUpdateResponse | undefined> => {
        const res = await nativeBridge<CertUpdateRequest, CertUpdateResponse>(
            'doNCertUpdate',
            request,
        );

        if (res.status === 'fail' || res.status === 'error') {
            const error = res.error!;
            if (options?.onError && error) {
                options.onError(error);
            } else {
                errorStore.setErrorInfo({ code: error.code, title: error.message });
            }
            return;
        }

        if (options?.onSuccess && res.payload) {
            options.onSuccess(res.payload);
            return;
        }

        return res.payload;
    };

    /**
     * 공동인증서 폐기
     * @param request
     * @returns
     */
    const doNCertDelete = async (
        request: CertDeleteRequest,
        options?: NativeBridgeOptions<CertDeleteResponse>,
    ): Promise<CertDeleteResponse | undefined> => {
        const res = await nativeBridge<CertDeleteRequest, CertDeleteResponse>(
            'doNCertDelete',
            request,
        );

        if (res.status === 'fail' || res.status === 'error') {
            const error = res.error!;
            if (options?.onError && error) {
                options.onError(error);
            } else {
                errorStore.setErrorInfo({ code: error.code, title: error.message });
            }
            return;
        }

        if (options?.onSuccess && res.payload) {
            options.onSuccess(res.payload);
            return;
        }

        return res.payload;
    };

    /**
     * 공동인증서 상세정보
     * @param request
     * @returns
     */
    const doNCertInfo = async (
        request: CertInfoRequest,
        options?: NativeBridgeOptions<CertInfoResponse>,
    ): Promise<CertInfoResponse | undefined> => {
        const res = await nativeBridge<CertInfoRequest, CertInfoResponse>('doNCertInfo', request);

        if (res.status === 'fail' || res.status === 'error') {
            const error = res.error!;
            if (options?.onError && error) {
                options.onError(error);
            } else {
                errorStore.setErrorInfo({ code: error.code, title: error.message });
            }
            return;
        }

        try {
            if (!res.payload) {
                throw new Error('doNCertInfo - not found payload');
            }

            if (typeof res.payload.info === 'string') {
                const decoded = decodeURIComponent(res.payload.info);
                res.payload.info = JSON.parse(decoded);
            }
        } catch (error) {
            console.error('doNCertInfo error :: ', error);
        }

        return res.payload;
    };

    /**
     * 공동인증서 비밀번호 변경
     * @param request
     * @returns
     */
    const doNCertPasswordChange = async (
        request: CertPasswordChangeRequest,
        options?: NativeBridgeOptions<CertPasswordChangeResponse>,
    ): Promise<CertPasswordChangeResponse | undefined> => {
        const res = await nativeBridge<CertPasswordChangeRequest, CertPasswordChangeResponse>(
            'doNCertPasswordChange',
            request,
        );

        if (res.status === 'fail' || res.status === 'error') {
            const error = res.error!;
            if (options?.onError && error) {
                options.onError(error);
            } else {
                errorStore.setErrorInfo({ code: error.code, title: error.message });
            }
            return;
        }

        if (options?.onSuccess && res.payload) {
            options.onSuccess(res.payload);
            return;
        }

        return res.payload;
    };

    return {
        doNCertIssue,
        doNCertSign,
        doNCertList,
        doNReqCertImport,
        doNReqCertImportSave,
        doNReqCertExportAuthNum,
        doNReqCertExportResult,
        doNCertUpdate,
        doNCertDelete,
        doNCertInfo,
        doNCertPasswordChange,
    };
};
